<template>
  <div>
    <validation-observer
      v-slot="{ invalid, valid, errors, ...rest }"
      ref="form"
    >
      <form @submit.prevent="submit">
        <section-header :title="sectionHeaderTitle" style="margin-bottom: 30px;">
          <c-button
            v-show="!editMode && showEditButton"
            key="add-btn"
            theme="primary"
            size="sm"
            icon="fa-regular fa-pen-to-square"
            @click="showEditMode"
          >Edit</c-button>

          <c-button
            v-show="editMode && showEditButton"
            key="save-btn"
            type="submit"
            theme="success"
            size="sm"
            icon="fa-regular fa-save"
            @click="submit"
            :showIndicator="showIndicator"
          >Save</c-button>

          <c-button
            v-show="editMode && showEditButton"
            key="cancel-btn"
            theme="default"
            size="sm"
            icon="fa-regular fa-xmark"
            @click="hideEditMode"
          >Cancel</c-button>

          <c-button-help
            v-if="helpButtonUrl"
            key="help-btn"
            :link="helpButtonUrl"
          />
        </section-header>

        <section-card
          :loading="isFetching"
          :loading-lines="5"
          :icon="pageTitleIcon"
          :title="pageTitle"
          :desc="infoDescription"
          :color="cardColor"
          :admin-only="adminOnly"
        >
          <!-- View mode -->
          <div class="ic-fields" v-show="!editMode">
            <div class="ic-field" v-for="(field, index) in fields" :key="`preview-${index}`">

              <!-- Checkbox: label left, enabled/disabled badge right -->
              <template v-if="field.type === 'checkbox'">
                <div class="ic-field__label">{{ field.label }}</div>
                <div>
                  <span class="ic-field__badge" :class="item[field.name] ? 'ic-field__badge--yes' : 'ic-field__badge--no'">
                    {{ item[field.name] ? '✓ Enabled' : '✕ Disabled' }}
                  </span>
                </div>
              </template>

              <!-- Text / textarea / editor: label left, value right -->
              <template v-else>
                <div class="ic-field__label">{{ field.label }}</div>
                <p v-if="item[field.name]" class="ic-field__value" v-html="item[field.name]"></p>
                <p v-else class="ic-field__empty">No data yet</p>
              </template>

            </div>
          </div>

          <!-- Edit mode -->
          <div class="ic-edit-fields" v-show="editMode">
            <c-select
              v-if="templates && templates.length"
              :empty-option="true"
              empty-option-label="- Select -"
              label="Select Template"
              v-model="templateId"
              :options="templates"
            />

            <!-- Optional guidance rendered right above the fields (e.g. how the
                 agenda text is parsed). Unused by most pages. -->
            <slot name="before-fields" />

            <fragment v-for="(item, index) in fields" :key="`field-${index}`">
              <validation-provider
                :vid="item.name"
                :rules="item.isRequired && 'required'"
                v-slot="{ errors }"
              >
                <c-input
                  v-if="item.type === 'text'"
                  :type="item.inputType ? item.inputType : 'text'"
                  :label="item.label"
                  :error="errors[0]"
                  :required-mark="item.isRequired"
                  v-model="currentItem[item.name]"
                  :help="item.help"
                />

                <c-textarea
                  v-if="item.type === 'textarea'"
                  :label="item.label"
                  :error="errors[0]"
                  :required-mark="item.isRequired"
                  :rows="item.rows"
                  v-model="currentItem[item.name]"
                />

                <c-editor
                  :id="`editor-${index}`"
                  v-if="item.type === 'editor'"
                  :label="item.label"
                  :error="errors[0]"
                  :required-mark="item.isRequired"
                  v-model="currentItem[item.name]"
                />

                <c-checkbox
                  v-if="item.type === 'checkbox'"
                  :label="item.label"
                  :error="errors[0]"
                  v-model="currentItem[item.name]"
                />
              </validation-provider>
            </fragment>

            <c-checkbox
              v-if="showSaveAsTemplate"
              v-model="currentItem.as_template"
              label="Save as Template"
            />
          </div>
        </section-card>

      </form>
    </validation-observer>
  </div>
</template>

<script>
import { fetchData } from '../../api/network'

export default {
  props: {
    sectionHeaderTitle: {
      type: String,
      default: ''
    },
    pageTitle: {
      type: String,
      default: ''
    },
    pageTitleIcon: {
      type: String,
      default: 'fa-circle-info',
    },
    cardColor: {
      type: String,
      default: '',
    },
    adminOnly: {
      type: Boolean,
      default: false,
    },
    showEditButton: {
      type: Boolean,
      default: true,
    },
    helpButtonUrl: {
      type: String,
      default: ''
    },
    infoDescription: {
      type: String,
      default: ''
    },
    isFetching: {
      type: Boolean,
      default: false
    },
    showIndicator: {
      type: Boolean,
      default: false
    },
    showSaveAsTemplate: {
      type: Boolean,
      default: false
    },
    templates: {
      type: Array,
      default: () => []
    },
    fields: {
      type: Array,
      default: () => []
    },
    item: {
      type: Object,
      default: () => {}
    },
    templateDataByIdUrl: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      editMode: false,
      currentItem: {},
      templateId: null
    }
  },
  watch: {
    'templateId': {
      handler(newVal) {
        const baseUrl = `${this.templateDataByIdUrl}/${newVal}`

        if (newVal) {
          fetchData(baseUrl).then((resp) => {
            const id = this.currentItem.id ? this.currentItem.id : null

            const data = { ...resp.data.fields }
            delete data.id
            this.currentItem = data

            this.fields.forEach((field, index) => {
              if (field.type === 'editor') {
                $(`#editor-${index}`).summernote('code', resp.data.fields[`${field.name}`])
              }
            })

            if (id) {
              this.currentItem.id = id
            }
          })
            .catch(() => {})
        }
      },
      deep: false
    }
  },
  methods: {
    showEditMode() {
      this.currentItem = { ...this.item };

      this.fields.forEach((field, index) => {
        if (field.type === 'editor') {
          $(`#editor-${index}`).summernote('code', this.currentItem[`${field.name}`]);
        }
      })

      this.editMode = true
    },

    hideEditMode() {
      this.editMode = false
      this.templateId = null
      this.currentItem = {}
      this.currentItem.as_template = false
    },

    submit() {
      this.$refs.form.validate().then(success => {
        if (!success) {
          return
        }

        this.$emit('edit', this.currentItem, this.$refs.form, this.hideEditMode)

        this.$nextTick(() => {
          this.$refs.form.reset()
        })
      })
    }
  }
}
</script>

<style lang="scss" scoped>
// View mode — two-column table layout
.ic-fields {
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 24px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.ic-field {
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  padding: 13px 0;
  border-bottom: 1px solid #f1f5f9;
  align-items: baseline;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }

  &:last-child {
    border-bottom: none;
  }

  &__label {
    font-size: 14px;
    font-weight: 700;
    color: #475569;
    line-height: 1.5;
  }

  &__value {
    font-size: 14px;
    color: #1e293b;
    line-height: 1.6;
    white-space: pre-wrap;
    margin: 0;
  }

  &__empty {
    font-size: 14px;
    color: #cbd5e1;
    font-style: italic;
    margin: 0;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border-radius: 20px;
    padding: 2px 10px;
    font-size: 11px;
    font-weight: 600;

    &--yes {
      background: #f0fdf4;
      color: #16a34a;
      border: 1px solid #bbf7d0;
    }

    &--no {
      background: #fef2f2;
      color: #dc2626;
      border: 1px solid #fecaca;
    }
  }
}

// Edit mode
.ic-edit-fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
</style>
